import { useDebounceFn } from '@vueuse/core'
import axios from 'axios'
import type { Contest } from '@/types/types'

type LegacyContestGame = {
  id: number
  name: string
}

type ContestGamesIndexResponse = {
  data: Array<{ id: number | string; name: string }>
  meta?: {
    current_page?: number
    last_page?: number
  }
}

const LEGACY_CONTEST_GAMES_PAGE_SIZE = 50
const SEARCH_DEBOUNCE_MS = 300

export const useMigratedFromLegacyContestGame = (contest: Ref<Contest>) => {
  const { t } = useI18n()
  const { isSupportConnection } = useLocalStorage()
  const notification = useNotification()

  const legacyContestGames = ref<LegacyContestGame[]>([])
  const legacyContestGamesPage = ref(0)
  const legacyContestGamesLastPage = ref(1)
  const isLoadingLegacyContestGames = ref(false)
  const isLoadingMoreLegacyContestGames = ref(false)
  const legacyContestGamesSearch = ref('')
  const selectedLegacyContestGameLabel = ref<string | null>(null)
  let fetchRequestId = 0

  const migratedFromContestGameId = computed({
    get: () => contest.value.migratedFromContestGameId ?? null,
    set: (value: number | null) => {
      contest.value.migratedFromContestGameId = value
      if (value === null) {
        selectedLegacyContestGameLabel.value = null
        return
      }
      selectedLegacyContestGameLabel.value =
        legacyContestGames.value.find((game) => game.id === value)?.name ??
        contest.value.migratedFromContestGame?.name ??
        selectedLegacyContestGameLabel.value
    }
  })

  const legacyContestGameOptions = computed(() => {
    const options = legacyContestGames.value
      .filter((game) => game.id !== contest.value.id)
      .map((game) => ({ id: game.id, name: game.name }))

    const selectedId = migratedFromContestGameId.value
    if (
      selectedId &&
      selectedId !== contest.value.id &&
      !options.some((game) => game.id === selectedId) &&
      selectedLegacyContestGameLabel.value
    ) {
      options.unshift({ id: selectedId, name: selectedLegacyContestGameLabel.value })
    }

    return options
  })

  const hasMoreLegacyContestGames = computed(
    () => legacyContestGamesPage.value > 0 && legacyContestGamesPage.value < legacyContestGamesLastPage.value
  )

  const mergeLegacyContestGames = (games: LegacyContestGame[], append: boolean) => {
    if (!append) {
      legacyContestGames.value = games
      return
    }

    const merged = new Map(legacyContestGames.value.map((game) => [game.id, game]))
    for (const game of games) {
      merged.set(game.id, game)
    }
    legacyContestGames.value = Array.from(merged.values())
  }

  const fetchLegacyContestGames = async ({
    page = 1,
    append = false,
    search = legacyContestGamesSearch.value
  }: {
    page?: number
    append?: boolean
    search?: string
  } = {}) => {
    if (!isSupportConnection.value) {
      return
    }

    if (append) {
      if (isLoadingMoreLegacyContestGames.value || isLoadingLegacyContestGames.value) {
        return
      }
      isLoadingMoreLegacyContestGames.value = true
    } else {
      isLoadingLegacyContestGames.value = true
    }

    const requestId = ++fetchRequestId

    try {
      const response = await axios.get<ContestGamesIndexResponse>('/contest-games', {
        params: {
          page,
          per_page: LEGACY_CONTEST_GAMES_PAGE_SIZE,
          isLegacy: true,
          ...(search ? { search } : {})
        }
      })

      if (requestId !== fetchRequestId) {
        return
      }

      const games = (response.data.data ?? []).map((game) => ({
        id: Number(game.id),
        name: game.name
      }))
      mergeLegacyContestGames(games, append)
      legacyContestGamesPage.value = response.data.meta?.current_page ?? page
      legacyContestGamesLastPage.value = response.data.meta?.last_page ?? page
    } catch {
      if (requestId !== fetchRequestId) {
        return
      }

      if (!append) {
        legacyContestGames.value = []
        legacyContestGamesPage.value = 0
        legacyContestGamesLastPage.value = 1
      }

      notification.create('error', {
        text: t('pages.contests.contestsCreatePage.global.form.fields.migratedFromContestGame.loadError')
      })
    } finally {
      if (append) {
        isLoadingMoreLegacyContestGames.value = false
      } else if (requestId === fetchRequestId) {
        isLoadingLegacyContestGames.value = false
      }
    }
  }

  const loadMoreLegacyContestGames = () => {
    if (!hasMoreLegacyContestGames.value) {
      return
    }

    fetchLegacyContestGames({
      page: legacyContestGamesPage.value + 1,
      append: true
    })
  }

  const searchLegacyContestGames = useDebounceFn((search: string) => {
    legacyContestGamesSearch.value = search
    fetchLegacyContestGames({ page: 1, append: false, search })
  }, SEARCH_DEBOUNCE_MS)

  const initMigratedFromLegacyContestGame = () => {
    if (contest.value.migratedFromContestGame?.name) {
      selectedLegacyContestGameLabel.value = contest.value.migratedFromContestGame.name
    }
    fetchLegacyContestGames()
  }

  return {
    isSupportConnection,
    migratedFromContestGameId,
    legacyContestGameOptions,
    hasMoreLegacyContestGames,
    isLoadingMoreLegacyContestGames,
    loadMoreLegacyContestGames,
    searchLegacyContestGames,
    initMigratedFromLegacyContestGame
  }
}
